import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { IStore } from 'Store/.';
import { bindActionCreators, Dispatch } from 'redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { getLocale } from 'Store/User/selectors';
import { getIsMobile } from 'Store/Screen/selectors';
import { IEventsFilter } from 'Pages/EventsPage/Redux/reducer';
import { ICategory, categories } from 'Components/MenuMain/MenuMainContent';
import { fetchFilteredEvents, fetchSearchedEvents } from 'Pages/EventsPage/Redux/thunks';
import EventsHeader from 'Pages/EventsPage/EventsHeader/EventsHeader';
import EventsFilter from 'Pages/EventsPage/EventsFilter/EventsFilter';
import EventsList from 'Pages/EventsPage/EventsList/EventsList';
import EventsItem from 'Pages/EventsPage/EventsItem/EventsItem';

import {
  setCategory,
  setMoreVisibleEvents,
  setBoxView,
  setListView,
  setFilterFirstDate,
  setFilterLastDate,
  setFilterCity,
  setFilterEmpty,
  setEventsIncreaseRatio,
  setIsSearchResult,
} from 'Pages/EventsPage/Redux/actions';

import {
  getVisibleEvents,
  getIsShowingAll,
  getIsFetching,
  getIsFiltered,
  getCategory,
  getIsBoxView,
  getCities,
  getFilter,
  getIsFetchFailed,
  getIsSearchResult,
} from 'Pages/EventsPage/Redux/selectors';

interface IPropsInternal {
  locale: string;
  isMobile: boolean;
  isFetching: boolean;
  isFetchFailed: boolean;
  isFiltered: boolean;
  isShowingAll: boolean;
  isBoxView: boolean;
  isSearchResult: boolean;
  category: ICategory;
  visibleEvents: Julius.Website.EventOverviewViewModel[];
  filter: IEventsFilter;
  filterCityOptions: string[];
  setBoxView: () => void;
  setListView: () => void;
  setCategory: (id: number) => void;
  setFilterFirstDate: (date: Date) => void;
  setFilterLastDate: (date: Date) => void;
  setFilterCity: (city: string) => void;
  setFilterEmpty: () => void;
  setMoreVisibleEvents: () => void;
  setEventsIncreaseRatio: (ratio: number) => void;
  setIsSearchResult: (isSearchResult: boolean) => void;
  fetchFilteredEvents: () => void;
  fetchSearchedEvents: (searchQuery: string) => void;
}

type Props = IPropsInternal & RouteComponentProps<{ searchQuery: string }>;

interface IState {
  isShowingMobileFilter: boolean;
}

export class EventsPageContainer extends React.PureComponent<Props, IState> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isShowingMobileFilter: false,
    };
  }

  componentDidMount() {
    const { searchQuery } = this.props.match.params;

    if (searchQuery) {
      this.props.setIsSearchResult(true);
      this.props.fetchSearchedEvents(searchQuery);
      return;
    } 

    const { isMobile, location: { pathname } } = this.props;
    const category = categories.find(c => c.localPath === pathname);
    const categoryID = category ? category.id : 0;
    this.props.setCategory(categoryID);

    if (isMobile) {
      this.props.setEventsIncreaseRatio(6);
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const prevSearch = this.props.match.params.searchQuery;
    const nextSearch = nextProps.match.params.searchQuery;

    if (nextSearch !== prevSearch) {
      this.props.setIsSearchResult(true);
      this.props.fetchSearchedEvents(nextSearch);
    }
  }

  onEventClick = (eventID: number) => {
    this.props.history.push(`/events/${ eventID }`);
  }

  onToggleMobileFilter = () => {
    this.setState({ isShowingMobileFilter: !this.state.isShowingMobileFilter });
  }

  filterFirstDate = (date: Date) => {
    this.props.setFilterFirstDate(date);
    this.props.fetchFilteredEvents();
  }

  filterLastDate = (date: Date) => {
    this.props.setFilterLastDate(date);
    this.props.fetchFilteredEvents();
  }

  filterCity = (value: string) => {
    this.props.setFilterCity(value);
    this.props.fetchFilteredEvents();
  }

  render() {
    return (
      <>
        <EventsHeader 
          category={ this.props.category }
          isBoxView={ this.props.isBoxView }
          isMobile={ this.props.isMobile }
          isShowingMobileFilter={ this.state.isShowingMobileFilter }
          isSearchResult={ this.props.isSearchResult }
          onSetBoxView={ this.props.setBoxView }
          onSetListView={ this.props.setListView }
          onToggleMobileFilter={ this.onToggleMobileFilter }
        />
        { !this.props.isSearchResult &&
          <EventsFilter 
            locale={ this.props.locale }
            isMobile={ this.props.isMobile }
            filter={ this.props.filter }
            filterCityOptions={ this.props.filterCityOptions }
            isShowingMobileFilter={ this.state.isShowingMobileFilter }
            onFilterFirstDate={ this.filterFirstDate }
            onFilterLastDate={ this.filterLastDate }
            onFilterCity={ this.filterCity }
          />
        }
        <EventsList 
          isFetching={ this.props.isFetching }
          isFiltered={ this.props.isFiltered }
          isFetchFailed={ this.props.isFetchFailed }
          isShowingAll={ this.props.isShowingAll }
          isBoxView={ this.props.isBoxView }
          visibleEvents={ this.props.visibleEvents }
          onSetMoreVisibleEvents={ this.props.setMoreVisibleEvents }
        >
          { this.props.visibleEvents.map(event => 
            <EventsItem 
              key={ event.key } 
              event={ event }
              isMobile={ this.props.isMobile }
              isBoxView={ this.props.isBoxView }
              onClick={ this.onEventClick }
            />)
          }
        </EventsList>
      </>
    );
  }
}

const mapStateToProps = (state: IStore) => {
  return {
    locale: getLocale(state),
    isMobile: getIsMobile(state),
    isFetching: getIsFetching(state),
    isFetchFailed: getIsFetchFailed(state),
    isFiltered: getIsFiltered(state),
    isShowingAll: getIsShowingAll(state),
    isBoxView: getIsBoxView(state),
    isSearchResult: getIsSearchResult(state),
    category: getCategory(state),
    visibleEvents: getVisibleEvents(state),
    filter: getFilter(state),
    filterCityOptions: getCities(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return bindActionCreators({
    setBoxView,
    setListView,
    setCategory,
    setMoreVisibleEvents,
    setFilterFirstDate,
    setFilterLastDate,
    setFilterCity,
    setFilterEmpty,
    setEventsIncreaseRatio,
    setIsSearchResult,
    fetchFilteredEvents,
    fetchSearchedEvents,
  }, dispatch);
};

export default compose<{}, {}>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(EventsPageContainer);


// WEBPACK FOOTER //
// ./src/pages/EventsPage/EventsPageContainer.tsx